import { useEffect, useState } from "react";
import { useNotify } from "ra-core";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { getSupabaseClient } from "../providers/supabase/supabase";

// Settings → Automations (docs/16 §D). The admin face of the automation_rules
// engine: the status→task/drip *bindings* (Campaign Settings owns the drip
// *sequences*, slice 3). This UI edits ONLY the rule rows (data). It does not
// touch execution semantics — drafting-for-approval, guardrails, the single
// suppression predicate, and no-rule-chaining all stay exactly as the engine
// runs them. Assigned role is stored as data; it does not grant permissions
// (that's slice-2 RBAC).

// Triggers the engine understands, and the condition key each one reads.
const TRIGGERS: { value: string; label: string; condKey: string }[] = [
  { value: "status_change", label: "Lead status change", condKey: "status" },
  { value: "temperature_change", label: "Lead temperature change", condKey: "temperature" },
  { value: "deal_stage_change", label: "Deal stage change", condKey: "deal_stage" },
];
const condKeyFor = (trigger: string) => TRIGGERS.find((t) => t.value === trigger)?.condKey ?? "status";

// lead_status / lead_temperature enum values (see the leads schema).
const STATUSES = ["prospect", "new_lead", "nurture", "qualified", "handoff", "not_interested", "ghosting", "dead", "lost", "became_buyer", "closed"];
const TEMPERATURES = ["hot", "warm", "cold"];
// The six D5 roles (stored as data on the task action; RBAC is slice 2).
const ROLES = ["admin", "acquisitions", "dispositions", "transaction_coordinator", "lead_manager", "data_manager"];
const PRIORITIES = ["low", "medium", "high", "urgent"];

// Action types with a structured editor. Anything else on a rule is preserved
// untouched (shown read-only) so we never corrupt an action we don't model.
const ACTION_TYPES = [
  "create_task", "enroll_drip", "draft_sms", "draft_email",
  "notify", "set_next_action_at", "set_reactivation_at",
  "assign_round_robin", "remove_from_drip",
];
const KNOWN = new Set(ACTION_TYPES);

type Rule = {
  id: number;
  name: string;
  trigger: string;
  condition: Record<string, any>;
  actions: any[];
  enabled: boolean;
  priority: number;
};
type Draft = Omit<Rule, "id"> & { id?: number };
const EMPTY: Draft = { name: "", trigger: "status_change", condition: { status: "new_lead" }, actions: [], enabled: true, priority: 10 };

const AutomationsPage = () => {
  const notify = useNotify();
  const [rules, setRules] = useState<Rule[]>([]);
  const [stages, setStages] = useState<{ key: string; name: string }[]>([]);
  const [templates, setTemplates] = useState<{ key: string; name: string; channel: string }[]>([]);
  const [draft, setDraft] = useState<Draft | null>(null);

  const load = async () => {
    const sb = getSupabaseClient();
    const [{ data: r }, { data: st }, { data: tp }] = await Promise.all([
      sb.from("automation_rules").select("*").order("priority").order("id"),
      sb.from("deal_stages").select("key,name").order("position"),
      sb.from("templates").select("key,name,channel").order("name"),
    ]);
    setRules((r as Rule[]) ?? []);
    setStages((st as any[]) ?? []);
    setTemplates((tp as any[]) ?? []);
  };
  useEffect(() => { load(); }, []);

  const toggle = async (rule: Rule) => {
    const { error } = await getSupabaseClient().from("automation_rules").update({ enabled: !rule.enabled }).eq("id", rule.id);
    if (error) notify(`Failed: ${error.message}`, { type: "error" }); else load();
  };
  const del = async (rule: Rule) => {
    if (!window.confirm(`Delete automation "${rule.name}"?`)) return;
    const { error } = await getSupabaseClient().from("automation_rules").delete().eq("id", rule.id);
    if (error) notify(`Failed: ${error.message}`, { type: "error" }); else { notify("Automation deleted", { type: "success" }); if (draft?.id === rule.id) setDraft(null); load(); }
  };

  const save = async () => {
    if (!draft) return;
    if (!draft.name.trim()) { notify("Name is required.", { type: "warning" }); return; }
    const sb = getSupabaseClient();
    const payload = {
      name: draft.name.trim(),
      trigger: draft.trigger,
      condition: draft.condition,
      actions: draft.actions,
      enabled: draft.enabled,
      priority: draft.priority,
    };
    if (draft.id) {
      const { error } = await sb.from("automation_rules").update(payload).eq("id", draft.id);
      if (error) { notify(`Save failed: ${error.message}`, { type: "error" }); return; }
      notify("Automation saved", { type: "success" });
    } else {
      const { error } = await sb.from("automation_rules").insert(payload);
      if (error) { notify(`Create failed: ${error.message}`, { type: "error" }); return; }
      notify("Automation created", { type: "success" });
    }
    setDraft(null); load();
  };

  // ---- draft field helpers ----
  const condKey = draft ? condKeyFor(draft.trigger) : "status";
  const condValue = draft ? draft.condition?.[condKey] ?? "" : "";
  const setTrigger = (trigger: string) => setDraft((d) => d && ({ ...d, trigger, condition: { [condKeyFor(trigger)]: "" } }));
  const setCond = (value: string) => setDraft((d) => d && ({ ...d, condition: { ...d.condition, [condKeyFor(d.trigger)]: value } }));
  const condOptions = draft?.trigger === "temperature_change" ? TEMPERATURES
    : draft?.trigger === "deal_stage_change" ? stages.map((s) => s.key)
    : STATUSES;

  const setAction = (i: number, patch: any) => setDraft((d) => d && ({ ...d, actions: d.actions.map((a, j) => (j === i ? { ...a, ...patch } : a)) }));
  const addAction = () => setDraft((d) => d && ({ ...d, actions: [...d.actions, { type: "create_task", name: "" }] }));
  const removeAction = (i: number) => setDraft((d) => d && ({ ...d, actions: d.actions.filter((_, j) => j !== i) }));

  const groups = TRIGGERS.map((t) => ({ ...t, rules: rules.filter((r) => r.trigger === t.value) }));
  const orphanTriggers = rules.filter((r) => !TRIGGERS.some((t) => t.value === r.trigger));

  return (
    <div className="p-2 max-w-5xl">
      <h1 className="text-xl font-semibold">Automations</h1>
      <div className="text-sm text-muted-foreground mb-1">
        Tasks and drips fired when a lead enters a status (or a deal enters a stage). These are the <em>bindings</em>; drip sequences live in Campaign Settings.
      </div>
      <div className="text-xs text-muted-foreground mb-4">
        Editing a rule changes what fires, not how — the engine still drafts for approval, never auto-sends, and applies its guardrails unchanged.
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        {/* Rules grouped by trigger */}
        <div className="space-y-4">
          {groups.map((g) => (
            <Card key={g.value}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0">
                <CardTitle className="text-base">{g.label} <span className="text-xs font-normal text-muted-foreground">({g.rules.length})</span></CardTitle>
                <Button size="sm" variant="outline" onClick={() => setDraft({ ...EMPTY, trigger: g.value, condition: { [g.condKey]: "" }, priority: 10 })}>Add</Button>
              </CardHeader>
              <CardContent className="space-y-1">
                {g.rules.map((r) => (
                  <div key={r.id} className={`flex items-center gap-2 rounded px-2 py-1.5 text-sm border-b last:border-0 ${draft?.id === r.id ? "bg-muted" : ""}`}>
                    <Switch checked={r.enabled} onCheckedChange={() => toggle(r)} />
                    <button className="text-left flex-1 min-w-0" onClick={() => setDraft({ ...r })}>
                      <div className="font-medium truncate">{r.name}</div>
                      <div className="text-xs text-muted-foreground">
                        when <span className="font-mono">{r.condition?.[g.condKey] ?? "—"}</span> · {r.actions?.length ?? 0} action{(r.actions?.length ?? 0) === 1 ? "" : "s"} · p{r.priority}
                      </div>
                    </button>
                    <Button variant="ghost" size="sm" onClick={() => del(r)}>Delete</Button>
                  </div>
                ))}
                {g.rules.length === 0 && <div className="text-sm text-muted-foreground py-1">No rules for this trigger.</div>}
              </CardContent>
            </Card>
          ))}
          {orphanTriggers.length > 0 && (
            <Card>
              <CardHeader><CardTitle className="text-base">Other triggers</CardTitle></CardHeader>
              <CardContent className="space-y-1">
                {orphanTriggers.map((r) => (
                  <div key={r.id} className="flex items-center gap-2 text-sm border-b last:border-0 py-1.5">
                    <Switch checked={r.enabled} onCheckedChange={() => toggle(r)} />
                    <button className="text-left flex-1" onClick={() => setDraft({ ...r })}><span className="font-medium">{r.name}</span> <span className="text-xs text-muted-foreground">· {r.trigger}</span></button>
                    <Button variant="ghost" size="sm" onClick={() => del(r)}>Delete</Button>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Editor */}
        <Card className="lg:sticky lg:top-4 self-start">
          <CardHeader><CardTitle className="text-base">{draft ? (draft.id ? "Edit automation" : "New automation") : "Automation"}</CardTitle></CardHeader>
          <CardContent>
            {!draft ? (
              <div className="text-sm text-muted-foreground">Select a rule to edit, or add one.</div>
            ) : (
              <div className="space-y-3">
                <label className="flex flex-col gap-1">
                  <span className="text-xs text-muted-foreground">Name</span>
                  <Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="e.g. New Lead → qualify" />
                </label>

                <div className="flex gap-3">
                  <label className="flex flex-col gap-1 flex-1">
                    <span className="text-xs text-muted-foreground">Trigger</span>
                    <select className="border rounded px-2 py-2 text-sm bg-background" value={draft.trigger} onChange={(e) => setTrigger(e.target.value)}>
                      {TRIGGERS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </label>
                  <label className="flex flex-col gap-1 flex-1">
                    <span className="text-xs text-muted-foreground">When value is</span>
                    <select className="border rounded px-2 py-2 text-sm bg-background" value={condValue} onChange={(e) => setCond(e.target.value)}>
                      <option value="">— select —</option>
                      {condOptions.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </label>
                  <label className="flex flex-col gap-1 w-20">
                    <span className="text-xs text-muted-foreground">Priority</span>
                    <Input type="number" value={draft.priority} onChange={(e) => setDraft({ ...draft, priority: +e.target.value })} />
                  </label>
                </div>

                <label className="flex items-center gap-2">
                  <Switch checked={draft.enabled} onCheckedChange={(v) => setDraft({ ...draft, enabled: v })} />
                  <span className="text-sm">Enabled</span>
                </label>

                {/* Actions builder */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Actions</span>
                    <Button size="sm" variant="outline" onClick={addAction}>Add action</Button>
                  </div>
                  {draft.actions.length === 0 && <div className="text-xs text-muted-foreground">No actions — add at least one.</div>}
                  {draft.actions.map((a, i) => (
                    <div key={i} className="rounded border p-2 space-y-2">
                      <div className="flex items-center gap-2">
                        <select className="border rounded px-2 py-1 text-sm bg-background" value={a.type} onChange={(e) => setDraft({ ...draft, actions: draft.actions.map((x, j) => j === i ? { type: e.target.value } : x) })}>
                          {(KNOWN.has(a.type) ? ACTION_TYPES : [...ACTION_TYPES, a.type]).map((t) => <option key={t} value={t}>{t}</option>)}
                        </select>
                        <Button variant="ghost" size="sm" className="ml-auto" onClick={() => removeAction(i)}>Remove</Button>
                      </div>
                      <ActionFields a={a} i={i} set={setAction} roles={ROLES} priorities={PRIORITIES} templates={templates} />
                    </div>
                  ))}
                </div>

                <div className="flex items-center gap-2 pt-1">
                  <Button onClick={save}>{draft.id ? "Save" : "Create"}</Button>
                  <Button variant="ghost" onClick={() => setDraft(null)}>Cancel</Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Per-action-type fields. Unknown types keep their raw JSON, read-only, so we
// never drop data the engine relies on.
const ActionFields = ({ a, i, set, roles, priorities, templates }: {
  a: any; i: number; set: (i: number, p: any) => void; roles: string[]; priorities: string[]; templates: { key: string; name: string; channel: string }[];
}) => {
  const L = ({ label, children }: { label: string; children: React.ReactNode }) => (
    <label className="flex flex-col gap-1"><span className="text-[11px] text-muted-foreground">{label}</span>{children}</label>
  );
  const sel = "border rounded px-2 py-1.5 text-sm bg-background";
  switch (a.type) {
    case "create_task":
      return (
        <div className="grid grid-cols-2 gap-2">
          <L label="Task name"><Input className="h-8" value={a.name ?? ""} onChange={(e) => set(i, { name: e.target.value })} /></L>
          <L label="Task type"><Input className="h-8" value={a.task_type ?? ""} placeholder="e.g. call" onChange={(e) => set(i, { task_type: e.target.value })} /></L>
          <L label="Priority"><select className={sel} value={a.priority ?? ""} onChange={(e) => set(i, { priority: e.target.value || undefined })}><option value="">—</option>{priorities.map((p) => <option key={p} value={p}>{p}</option>)}</select></L>
          <L label="Assigned role"><select className={sel} value={a.assigned_role ?? ""} onChange={(e) => set(i, { assigned_role: e.target.value || undefined })}><option value="">—</option>{roles.map((r) => <option key={r} value={r}>{r}</option>)}</select></L>
          <L label="Deadline (+days)"><Input className="h-8" type="number" value={a.offset_days ?? ""} onChange={(e) => set(i, { offset_days: e.target.value === "" ? undefined : +e.target.value })} /></L>
        </div>
      );
    case "enroll_drip":
      return (
        <div className="grid grid-cols-2 gap-2 items-end">
          <L label="Drip (campaign key)"><Input className="h-8" value={a.drip ?? ""} placeholder="e.g. quarterly_nurture" onChange={(e) => set(i, { drip: e.target.value })} /></L>
          <label className="flex items-center gap-2 pb-1.5"><Switch checked={!!a.stop_on_engagement} onCheckedChange={(v) => set(i, { stop_on_engagement: v || undefined })} /><span className="text-xs">Stop on engagement</span></label>
        </div>
      );
    case "draft_sms":
    case "draft_email": {
      const ch = a.type === "draft_sms" ? "sms" : "email";
      return (
        <L label="Template"><select className={sel} value={a.template ?? ""} onChange={(e) => set(i, { template: e.target.value || undefined })}>
          <option value="">— select —</option>
          {templates.filter((t) => t.channel === ch).map((t) => <option key={t.key} value={t.key}>{t.name} ({t.key})</option>)}
        </select></L>
      );
    }
    case "notify":
      return <L label="Note (optional)"><Input className="h-8" value={a.name ?? ""} onChange={(e) => set(i, { name: e.target.value })} /></L>;
    case "set_next_action_at":
      return (
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2"><Switch checked={!!a.from_cadence} onCheckedChange={(v) => set(i, { from_cadence: v || undefined, value: v ? undefined : a.value })} /><span className="text-xs">From cadence</span></label>
          {!a.from_cadence && <L label="Value"><Input className="h-8 w-28" value={a.value ?? ""} placeholder="now" onChange={(e) => set(i, { value: e.target.value || undefined })} /></L>}
        </div>
      );
    case "set_reactivation_at":
      return <L label="Reactivate in (+days)"><Input className="h-8 w-28" type="number" value={a.offset_days ?? ""} onChange={(e) => set(i, { offset_days: e.target.value === "" ? undefined : +e.target.value })} /></L>;
    case "assign_round_robin":
    case "remove_from_drip":
      return <div className="text-xs text-muted-foreground">No parameters.</div>;
    default:
      return <div className="text-[11px] text-muted-foreground font-mono break-all">Preserved as-is: {JSON.stringify(a)}</div>;
  }
};

AutomationsPage.path = "/settings/automations";
export default AutomationsPage;
